import { type CSSProperties } from 'react';
import clsx from 'clsx';
import { splitCategoryEmoji } from '@budget/core';

// Pill badge used to label things by category — Links page uses it for a
// link's purpose, Transactions page uses it for a txn's budget category.
//
// The leading slot prefers an emoji extracted from the label (sheet
// categories now lead with one, e.g. "🍔 Food & Beverages") and falls back
// to a colored dot when only a color is provided. Both renderings occupy
// the same fixed-width slot so the label sits at the same x-offset across
// emoji'd, dot-only, and uncolored variants.

interface CategoryBadgeProps {
  label: string;
  color?: string | null;
  className?: string;
  /** Override the leading slot. Pass `false` to suppress emoji + dot. */
  showDot?: boolean;
  /**
   * Strip the pill chrome (background + padding + border) and render just
   * the icon and label inline. Color, when provided, still tints the text
   * so the category's identity is preserved.
   */
  naked?: boolean;
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium';
const NAKED_BASE = 'inline-flex items-center gap-1.5 text-xs font-medium';
const SLOT = 'w-4 flex-none inline-flex items-center justify-center';

export function CategoryBadge({
  label,
  color,
  className,
  showDot = true,
  naked = false,
}: CategoryBadgeProps) {
  const { emoji, name: bareLabel } = splitCategoryEmoji(label);
  const slot = showDot
    ? emoji
      ? (
          <span aria-hidden className={clsx(SLOT, 'text-[13px] leading-none')}>
            {emoji}
          </span>
        )
      : color
        ? (
            <span className={SLOT}>
              <span
                className="w-1.5 h-1.5 rounded-full dot-glow"
                style={{ backgroundColor: color, '--dot-color': color } as CSSProperties}
              />
            </span>
          )
        : null
    : null;

  if (naked) {
    return (
      <span
        className={clsx(NAKED_BASE, !color && 'text-fg-secondary', className)}
        style={color ? { color } : undefined}
      >
        {slot}
        {bareLabel}
      </span>
    );
  }

  if (!color) {
    return (
      <span
        className={clsx(
          PILL_BASE,
          'bg-subtle border border-border text-fg-secondary',
          className
        )}
      >
        {slot}
        {bareLabel}
      </span>
    );
  }
  return (
    <span
      className={clsx(PILL_BASE, className)}
      style={{
        // color-mix derives a translucent tint from any source — hex literals
        // OR var(--berg-*) references — so the same component works for both
        // the assigned-category palette and the brand tokens.
        backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`,
        color,
      }}
    >
      {slot}
      {bareLabel}
    </span>
  );
}
